import React, { useState } from 'react';
import styled from 'styled-components';

import { Command, tabActions } from '@store/index';
import { Card } from '@common/Card';
import { useDispatch } from 'react-redux';
import { Option } from '@common/BucketInput/BucketField';
import { useTheme } from '../../../../styles';
import { AttachMoneyOutlined } from '@mui/icons-material';
import { NodeInputExpression } from '../NodeInput';
import { NodeField } from '@components/Flow/Nodes';

export const TTProduct =
  'The name of the Telnyx product you want to check pricing for:\n\n- sms\n- mms\n- sip\n- voice\n- numbers\n- storage\n- iot\n- edge\n- verify\n- fax\n- video\n- networking\n- numberLookup\n- inference\n\nIt is recommended to create an enum argument named "product".\n\nAt runtime, the LLM will generate this argument and you can use it as an expression for this field.';

export const TTCountryCode =
  'The country code can be specified in ALPHA-2 format to get pricing specific to that country. This is not supported by all products.\n\nIt is recommended to create an argument named "countryCode" with a description of:\n\n"Output an ALPHA-2 country code".\n\nAt runtime, the LLM will generate this argument and you can use it as an expression for this field.';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 0;
`;

interface Props {
  command: Command;
  field: NodeField;
}

export const PricingCalculator: React.FC<Props> = ({ command, field }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const id = field.id;
  const state = command.state[id];
  const product = state.product;
  const countryCode = state.countryCode;
  const enabled = state.enabled;

  const [collapsed, setCollapsed] = useState(!enabled);

  const toggleEnabled = (newVal: boolean) => {
    dispatch(tabActions.setSaved(false));

    dispatch(
      tabActions.nodeInputNestedUpdate({
        nodeId: command.node.id,
        fieldId: field.id,
        nestedFieldName: 'enabled',
        value: newVal,
      })
    );

    if (newVal) setCollapsed(false);
  };

  const productOptions: Option[] = [
    { label: 'SMS', value: 'sms' },
    { label: 'MMS', value: 'mms' },
    { label: 'Elastic SIP Trunking', value: 'sip' },
    { label: 'Call Control & TeXML', value: 'voice' },
    { label: 'Global Numbers', value: 'numbers' },
    { label: 'Cloud Storage', value: 'storage' },
    { label: 'IoT SIM', value: 'iot' },
    { label: 'Edge Routers', value: 'edge' },
    { label: 'Verify', value: 'verify' },
    { label: 'Fax', value: 'fax' },
    { label: 'Video', value: 'video' },
    { label: 'Networking', value: 'networking' },
    { label: 'Number Lookup', value: 'numberLookup' },
    { label: 'Inference', value: 'inference' },
  ];

  const countryCodeOptions: Option[] = [
    { label: 'United States', value: 'us' },
    { label: 'Canada', value: 'ca' },
    { label: 'France', value: 'fr' },
    { label: 'Germany', value: 'de' },
    { label: 'Spain', value: 'es' },
    { label: 'United Kingdom', value: 'gb' },
  ];

  const body = (
    <Container>
      <NodeInputExpression
        field={field}
        command={command}
        allowToolArgs
        options={productOptions}
        override={{
          title: 'Telnyx Product',
          tooltip: TTProduct,
          value: product,
          optionType: true,
          update: (fieldName, value) => {
            dispatch(
              tabActions.nodeInputNestedUpdate({
                nodeId: command.node.id,
                fieldId: field.id,
                nestedFieldName: 'product',
                fieldName,
                value,
              })
            );
          },
        }}
      />
      <NodeInputExpression
        field={field}
        command={command}
        allowToolArgs
        options={countryCodeOptions}
        override={{
          title: 'Country Code',
          tooltip: TTCountryCode,
          value: countryCode,
          optionType: true,
          update: (fieldName, value) => {
            dispatch(
              tabActions.nodeInputNestedUpdate({
                nodeId: command.node.id,
                fieldId: field.id,
                nestedFieldName: 'countryCode',
                fieldName,
                value,
              })
            );
          },
        }}
      />
    </Container>
  );

  return (
    <Card
      title='Pricing Calculator'
      collapsed={collapsed}
      setCollapsed={setCollapsed}
      enabled={enabled}
      setEnabled={toggleEnabled}
      icon={<AttachMoneyOutlined sx={{ color: theme.telnyx.blue }} />}
      description={field.description}
      descriptionBorder
      body={body}
    />
  );
};
